import axios from 'axios';
import { PlaceAutocomplete } from '../google/PlaceAutocomplete';
import { PlaceDetail, Result } from '../google/PlaceDetail';
import Place from '../google/Place';
import { ApiConfig, getUseAPI } from './api';
import { Candidate } from '../../components/search/Candidate';
import { Lang } from '../../states/query';

/**
 * GooglePlacesAPIから取得した住所情報を整形する
 * @param baseAddress
 * @returns
 */
export const formatAddress = (baseAddress: string) => {
  // TODO: 日本語版のみを想定しているので、今後英語版が来たら対応する
  const matchList = baseAddress.match(/日本、(.*)/);
  if(matchList && Array.isArray(matchList) && matchList.length > 1 ) {
    return matchList[1];
  }
  else {
    return baseAddress;
  }
}

/**
 * Google Places APIから候補を取得する処理。
 * @param input テキストボックスで入力した値。
 * @param lang 言語指定（ja/en)
 * @param optionConfig
 */
export async function getAutocomplete (input: string, lang: string = 'ja', optionConfig?: ApiConfig) {
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const config = {
    ...useAPIConfig,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    input,
    // 地点候補の言語指定。
    language: lang,
    // 日本のデータのみ取得するよう指定。
    components: 'country:jp',
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`/places/autocomplete`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    // 施設（地点）のデータを取得する。
    const placeAutocomplete: PlaceAutocomplete = response.data;
    // 取得した施設（地点）のデータを画面上に表示する候補の形式に変換する。
    const candidates = placeAutocomplete.predictions.map((p) => {
      const name = p.structured_formatting ? p.structured_formatting.main_text : p.description;
      return new Candidate(name, p.place_id, 'place', undefined, undefined, formatAddress(p.description));
    })
    return candidates;
  } catch (err) {
    throw err;
  }
}

/**
 * 地点詳細（/places/details）を取得する。
 * （AutoCompleteで取得した地点の緯度経度を取得するために利用する。）
 * @param place_id ユーザーが指定した地点のID。（AutoCompleteで取得したplace_id）
 * @param optionConfig
 */
export async function getPlaceDetail (place_id: string, optionConfig?: ApiConfig) {
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const config = {
    ...useAPIConfig,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    place_id,
    fields: 'geometry,address_components,type,name,formatted_address',
    // テキスト部分はログ記録時のみ利用するため、日本語で固定とする。（2020/07/15現在。）
    language: 'ja',
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`/places/details`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }
    // Place Detailsのレスポンス部分。
    const data: PlaceDetail = response.data;
    // 施設（地点）の詳細データをインスタンス化して返す。
    return new Place(data.result);
  } catch (err) {
    throw err;
  }
}

/**
 * 位置情報より地点詳細情報を取得する（/places/geocode/）
 */
export async function getGeocode(latlng: string, lang: Lang = Lang.ja, optionConfig?: ApiConfig) {
  // 使用するAPI
  const useAPIConfig: ApiConfig = getUseAPI();
  const config = {
    ...useAPIConfig,
    ...optionConfig,
  };
  const instance = axios.create(config);
  const params = {
    latlng,
    language: lang
  };
  // あとで見直す（2020/02/20現在）
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await instance.get(`/places/geocode`, { params });

    // ステータスコードが200ではない場合。
    if (response.status !== 200) {
      throw new Error('Server Error');
    }

    // 施設（地点）の詳細データを取得する。
    return response.data.results.map((x: Result)=>{
      return new Place(x);
    });
  } catch (err) {
    throw err;
  }
}
